import React, { useEffect, useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../firebase";
import { addDoc, collection, doc, getDoc, Timestamp } from "firebase/firestore";
import Swal from "sweetalert2";

import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    CircularProgress,
    InputAdornment,
    IconButton
} from "@mui/material";

import { 
    EmailOutlined, 
    LockOutlined, 
    Visibility, 
    VisibilityOff 
} from '@mui/icons-material';

function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [systemDetails, setSystemDetails] = useState({});

    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        background: '#fff',
        color: '#000'
    });

    useEffect(() => {
        setSystemDetails({
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
            language: navigator.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        });

        const savedEmail = localStorage.getItem("email");
        const savedPassword = localStorage.getItem("password");
        if (savedEmail && savedPassword) {
            setEmail(savedEmail);
            setPassword(savedPassword);
            login(savedEmail, savedPassword, true);
        }
    }, []);

    const login = async (emailParam, passwordParam, auto = false) => {
        setLoading(true);
        try {
            const userEmail = auto ? emailParam : email;
            const userPassword = auto ? passwordParam : password;

            await signInWithEmailAndPassword(auth, userEmail, userPassword);
            const user = auth.currentUser;

            if (user) {
                const docRef = doc(db, "users", user.uid);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists() && docSnap.data().role === "Admin") {
                    if (!auto) {
                        localStorage.setItem("email", email);
                        localStorage.setItem("password", password);
                    }

                    await addDoc(collection(db, "logindetails"), {
                        uid: user.uid,
                        username: docSnap.data().name,
                        systemDetails,
                        createdTime: Timestamp.now()
                    });

                    Toast.fire({ icon: "success", title: "Welcome Back!" });
                    window.location.href = "/home";
                } else {
                    Toast.fire({ icon: "error", title: "Unauthorized Access" });
                    auth.signOut();
                    localStorage.clear();
                }
            }
        } catch (error) {
            if (!auto) Toast.fire({ icon: "error", title: "Invalid Credentials" });
            localStorage.clear();
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ 
            minHeight: "100vh", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center",
            background: "linear-gradient(45deg, #FF512F 0%, #F09819 51%, #FF512F 100%)",
            backgroundSize: "200% auto",
            animation: "gradient 5s ease infinite",
            "@keyframes gradient": {
                "0%": { backgroundPosition: "0% 50%" },
                "50%": { backgroundPosition: "100% 50%" },
                "100%": { backgroundPosition: "0% 50%" },
            },
            px: 2
        }}>
            <Paper 
                elevation={0}
                sx={{
                    width: "100%",
                    maxWidth: 420,
                    p: { xs: 4, md: 6 },
                    borderRadius: 6,
                    textAlign: "center",
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(10px)",
                    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                }}
            >
                {/* Branding */}
                <Box mb={4}>
                    <img src="/logo.jpg" alt="Logo" style={{ height: 70, borderRadius: '12px', marginBottom: '16px' }} />
                    <Typography variant="h4" sx={{ fontWeight: 800, color: "#333", letterSpacing: -1 }}>
                        Fintech
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666", fontWeight: 500 }}>
                        Finance Services
                    </Typography>
                </Box>

                <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: "#444" }}>
                    Sign In
                </Typography>

                {/* Form Fields */}
                <Box component="form" noValidate>
                    <TextField
                        fullWidth
                        placeholder="Email Address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        sx={{ mb: 2 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <EmailOutlined sx={{ color: 'orange' }} />
                                </InputAdornment>
                            ),
                            sx: { borderRadius: 3, backgroundColor: "#f9f9f9" }
                        }}
                    />
                    <TextField
                        fullWidth
                        placeholder="Password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        sx={{ mb: 4 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockOutlined sx={{ color: 'orange' }} />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                            sx: { borderRadius: 3, backgroundColor: "#f9f9f9" }
                        }}
                    />

                    <Button
                        fullWidth
                        variant="contained"
                        onClick={() => login()}
                        disabled={loading}
                        sx={{
                            py: 1.8,
                            borderRadius: 3,
                            fontSize: "1rem",
                            fontWeight: "bold",
                            textTransform: "none",
                            background: "linear-gradient(90deg, #FF512F 0%, #DD2476 100%)",
                            boxShadow: "0 10px 20px rgba(255, 81, 47, 0.3)",
                            '&:hover': {
                                background: "linear-gradient(90deg, #DD2476 0%, #FF512F 100%)",
                            }
                        }}
                    >
                        {loading ? <CircularProgress size={26} color="inherit" /> : "Access Dashboard"}
                    </Button>
                </Box>

                <Typography variant="caption" sx={{ mt: 5, display: "block", color: "#999" }}>
                    © {new Date().getFullYear()}  Finance Services
                </Typography>
            </Paper>
        </Box>
    );
}

export default Login;